import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useLang } from "../contexts/LanguageContext";
import MasonryFeed from "../components/MasonryFeed";
import ShareMenu from "../components/ShareMenu";

export default function HubDetail() {
  const { slug } = useParams();
  const { t } = useLang();
  const [hub, setHub] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    setHub(null);
    setErr("");
    api
      .get(`/hubs/${slug}`)
      .then(({ data }) => setHub(data))
      .catch((e) => setErr(formatApiError(e.response?.data?.detail) || e.message));
  }, [slug]);

  useEffect(() => {
    if (!hub) return;
    const prevTitle = document.title;
    document.title = `${hub.label} — ${t("hubs_title")} · Social Gallery`;
    const touched = [];
    const setMeta = (name, attr, value) => {
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      const prior = el ? el.getAttribute("content") : null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
        touched.push({ el, prior: null, created: true });
      } else {
        touched.push({ el, prior, created: false });
      }
      el.setAttribute("content", value);
    };
    const ogImg = `${process.env.REACT_APP_BACKEND_URL}/api/hubs/${hub.slug}/og.png`;
    const noun = hub.count === 1 ? "frame" : "frames";
    const desc = `${hub.count} ${noun} shot on ${hub.label}, curated on Social Gallery.`;
    setMeta("description", "name", desc);
    setMeta("og:title", "property", `${hub.label} — Gear Hub`);
    setMeta("og:description", "property", desc);
    setMeta("og:image", "property", ogImg);
    setMeta("twitter:title", "name", `${hub.label} — Gear Hub`);
    setMeta("twitter:image", "name", ogImg);
    return () => {
      document.title = prevTitle;
      touched.forEach(({ el, prior, created }) => {
        if (created) el.remove();
        else if (prior !== null) el.setAttribute("content", prior);
      });
    };
  }, [hub, t]);

  if (err)
    return (
      <div
        className="mx-auto max-w-[1600px] px-6 sm:px-10 py-14 font-mono text-sm text-red-400"
        data-testid="hub-detail-error"
      >
        {err}
      </div>
    );
  if (!hub)
    return (
      <div className="p-10 font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">
        {t("loading")}
      </div>
    );

  return (
    <div className="mx-auto max-w-[1600px] px-6 sm:px-10 py-14" data-testid="hub-detail-page">
      <Link
        to="/c"
        className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-zinc-200"
        data-testid="hub-back-link"
      >
        <ChevronLeft size={14} /> {t("hubs_title")}
      </Link>

      <header className="pt-6 pb-10 border-b border-zinc-900 flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-zinc-500">
            {t("hubs_eyebrow_detail")}
          </div>
          <h1
            className="mt-3 font-serif text-5xl sm:text-6xl text-zinc-50 font-light tracking-tighter leading-[0.95]"
            data-testid="hub-detail-label"
          >
            {hub.label}
          </h1>
          <div className="mt-4 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-400">
            <span data-testid="hub-detail-count">{hub.count}</span>{" "}
            {hub.count === 1 ? t("frame") : t("frames")}
          </div>
        </div>
        <ShareMenu
          url={`${process.env.REACT_APP_BACKEND_URL}/api/hubs/${hub.slug}/share`}
          title={`${hub.label} — Gear Hub · Social Gallery`}
          testid="hub-share"
        />
      </header>

      <div className="pt-10">
        {hub.photos.length === 0 ? (
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">
            {t("no_photos_yet")}
          </div>
        ) : (
          <MasonryFeed photos={hub.photos} />
        )}
      </div>
    </div>
  );
}
